<script lang="ts">
  import { untrack } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import ScheduleTimeFields from "../../../../../../../../../_shared/_modules/schedule-time-fields"
  import CreateWorkflowActionBox from "../../../create-workflow-action-box"
  import CreateWorkflowStepFrame from "../../../create-workflow-step-frame"
  import StepActionCTA from "../../../../../../../_shared/_modules/step-action-cta"
  import { addMinutes } from "../../../../../../../../../_shared/_logic/date-math"
  import {
    buildEmployeeOptions,
    hasOverlap,
    statusLabels,
    toInputValue,
    toIsoString,
  } from "../../../../../../../../../_shared/_logic/form-state"
  import type {
    AppointmentCreateInit,
    AppointmentCreateWorkflowEvent,
    AppointmentGeneralOutput,
    AppointmentPlanningOutput,
  } from "../../../../_logic/create-appointment-contract"

  type Props = {
    init: AppointmentCreateInit
    general: AppointmentGeneralOutput
    output?: AppointmentPlanningOutput
    readOnly?: boolean
    onBack: () => void
    onEvent: (event: AppointmentCreateWorkflowEvent) => void | Promise<void>
    onError?: (message: string | null) => void
  }

  const {
    init,
    general,
    output = undefined,
    readOnly = false,
    onBack,
    onEvent,
    onError = () => {},
  }: Props = $props()

  const initialPlanning = untrack(() => {
    const fallbackEnd =
      init.defaultEnd ?? (init.defaultStart ? addMinutes(init.defaultStart, 60) : null)
    const startAt = output?.startAt ?? toInputValue(init.defaultStart)
    const endAt = output?.endAt ?? toInputValue(fallbackEnd)

    return {
      employeeId: output?.employeeId ?? init.defaultEmployeeId ?? "",
      flexType: output?.flexType ?? "HARD",
      status: output?.status ?? init.defaultStatus ?? "RESERVED",
      startAt,
      endAt,
      timeframeStartAt: output?.timeframeStartAt ?? startAt,
      timeframeEndAt: output?.timeframeEndAt ?? endAt,
    }
  })

  const employeeOptions = $derived.by(() => {
    const selectedWorkOrder =
      general.selectedWorkOrder?.workOrder ??
      init.workOrders.find((workOrder) => workOrder.id === general.workOrderId) ??
      null
    const selectedWorkOrderEmployeeIds =
      selectedWorkOrder?.allowed_employee_ids?.filter(Boolean) ?? []
    const allowedEmployeeIds =
      general.workOrderSource === "existing"
        ? selectedWorkOrderEmployeeIds.length
          ? selectedWorkOrderEmployeeIds
          : null
        : init.candidateEmployeeIds

    return buildEmployeeOptions(
      allowedEmployeeIds == null
        ? init.employees
        : init.employees.filter((employee) => allowedEmployeeIds.includes(employee.id)),
    )
  })
  const flexTypeOptions = [
    { name: "Festtermin", value: "HARD" as ScheduleEventFlexType },
    { name: "Flextermin", value: "SOFT" as ScheduleEventFlexType },
  ]
  const statusOptions = (["RESERVED", "CONFIRMED"] as ScheduleEventStatus[]).map((value) => ({
    name: statusLabels[value] ?? value,
    value,
  }))

  let employeeId = $state(initialPlanning.employeeId)
  let flexType = $state<ScheduleEventFlexType | "">(initialPlanning.flexType)
  let status = $state<ScheduleEventStatus | "">(initialPlanning.status)
  let startAt = $state(initialPlanning.startAt)
  let endAt = $state(initialPlanning.endAt)
  let timeframeStartAt = $state<string | null>(initialPlanning.timeframeStartAt)
  let timeframeEndAt = $state<string | null>(initialPlanning.timeframeEndAt)
  let planningKind = $state<"single" | "series">("single")
  let seriesStartTime = $state("")
  let seriesRangeStart = $state<Date | null>(null)
  let seriesRangeEnd = $state<Date | null>(null)
  let seriesPattern = $state<"DAILY" | "WEEKDAYS" | "WEEKLY">("WEEKLY")
  let seriesWeekdays = $state<number[]>([])
  let seriesIgnoreConflicts = $state(true)

  const startIso = $derived(toIsoString(startAt))
  const endIso = $derived(toIsoString(endAt))
  const startDate = $derived(startIso ? new Date(startIso) : null)
  const endDate = $derived(endIso ? new Date(endIso) : null)
  const startInPast = $derived(Boolean(startDate && startDate.getTime() < Date.now()))
  const validEmployeeIds = $derived(
    new Set(employeeOptions.map((option) => String(option.value)).filter(Boolean)),
  )
  const validFlexTypes = new Set<ScheduleEventFlexType>(["HARD", "SOFT"])
  const validStatuses = new Set<ScheduleEventStatus>(["RESERVED", "CONFIRMED"])
  const hasConflict = $derived(
    Boolean(
      startIso &&
      endIso &&
      employeeId &&
      hasOverlap(init.scheduleEvents, startIso, endIso, employeeId, null, {
        flexType: flexType as ScheduleEventFlexType,
        eventType: "APPOINTMENT",
      }),
    ),
  )
  const draftDurationMinutes = $derived(
    Number(general.newWorkOrderDraft?.estimated_duration_min || 0) || null,
  )
  const autoDurationMinutes = $derived(
    Math.max(
      15,
      general.selectedWorkOrder?.workOrder?.estimated_duration_min ??
        draftDurationMinutes ??
        init.workOrders.find((order) => order.id === general.workOrderId)?.estimated_duration_min ??
        60,
    ),
  )
  const structuralValidationMessage = $derived.by(() => {
    if (!employeeId || !validEmployeeIds.has(employeeId))
      return "Bitte einen Mitarbeiter auswählen."

    if (!validFlexTypes.has(flexType as ScheduleEventFlexType))
      return "Bitte eine Terminart auswählen."

    if (!validStatuses.has(status as ScheduleEventStatus)) return "Bitte einen Status auswählen."

    if (!startIso || !endIso || !startDate || !endDate)
      return "Bitte gültige Start- und Endzeiten wählen."

    if (startDate >= endDate) return "Endzeit muss nach der Startzeit liegen."

    if (flexType === "SOFT") {
      const timeframeStartIso = toIsoString(timeframeStartAt)
      const timeframeEndIso = toIsoString(timeframeEndAt)

      if (!timeframeStartIso || !timeframeEndIso)
        return "Bitte ein gültiges Flex-Zeitfenster wählen."
    }

    return null
  })
  const canComplete = $derived(!structuralValidationMessage)

  const complete = () => {
    onError(null)

    if (structuralValidationMessage) {
      onError(structuralValidationMessage)

      return
    }

    void onEvent({
      type: "planning.completed",
      output: {
        employeeId,
        flexType: flexType as ScheduleEventFlexType,
        status: status as ScheduleEventStatus,
        startAt,
        endAt,
        timeframeStartAt: flexType === "SOFT" ? timeframeStartAt : startAt,
        timeframeEndAt: flexType === "SOFT" ? timeframeEndAt : endAt,
      },
    })
  }
</script>

<CreateWorkflowStepFrame
  title="Planung"
  description="Legen Sie fest, wer den Termin fährt, wie verbindlich er ist und wann er stattfinden soll."
  helpModalTextId="appointmentCreatePlanning"
>
  <CreateWorkflowActionBox label="Mitarbeiter und Status">
    <p>Festtermin ist ein konkreter Termin. Flextermin wird als Zeitfenster angekündigt.</p>
    <div class="create-workflow-field-grid">
      <Input
        type="select"
        options={employeeOptions}
        placeholder="Mitarbeiter"
        bind:value={employeeId}
        surfaceTone="dark"
        disabled={readOnly}
      />
      <Input
        type="select"
        options={flexTypeOptions}
        placeholder="Terminart"
        bind:value={flexType}
        surfaceTone="dark"
        disabled={readOnly}
      />
      <Input
        type="select"
        options={statusOptions}
        placeholder="Status"
        bind:value={status}
        surfaceTone="dark"
        disabled={readOnly}
      />
    </div>
  </CreateWorkflowActionBox>
  <ScheduleTimeFields
    bind:startValue={startAt}
    bind:endValue={endAt}
    bind:timeframeStartValue={timeframeStartAt}
    bind:timeframeEndValue={timeframeEndAt}
    bind:planningKind
    bind:seriesStartTime
    bind:seriesRangeStart
    bind:seriesRangeEnd
    bind:seriesPattern
    bind:seriesWeekdays
    bind:seriesIgnoreConflicts
    allowSeries={false}
    flexType={flexType as ScheduleEventFlexType}
    {employeeId}
    workingHoursByUser={init.workingHoursByUser}
    flexSlotMinutes={init.flexSlotMinutes}
    flexSlotsPerDay={init.flexSlotsPerDay}
    autoEndEnabled={flexType !== "SOFT"}
    formLocked={readOnly}
    {autoDurationMinutes}
    {startInPast}
    {hasConflict}
    layout="actionBox"
    actionBoxLabel="Zeitpunkt"
  />
  {#if !readOnly}
    <div class="step-actions">
      <CTA type="secondaryButton" content="Zurück" onClick={onBack} />
      <StepActionCTA
        type="primaryButton"
        content="Weiter zur Ankündigung"
        disabled={!canComplete}
        disabledReason={structuralValidationMessage}
        onClick={complete}
      />
    </div>
  {/if}
</CreateWorkflowStepFrame>

<style>
  .step-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: 0.4rem;
  }
</style>
